/**
 * Audio analysis utilities for detecting sound content and audio quality
 */

/**
 * Detects if audio buffer contains sound above a threshold using RMS analysis
 * @param audioBuffer - The AudioBuffer to analyze
 * @param threshold - RMS threshold for sound detection (default: 0.01)
 * @returns true if audio contains sound, false if silent
 */
export const hasAudioContent = (audioBuffer: AudioBuffer, threshold: number = 0.01): boolean => {
	const numChannels = audioBuffer.numberOfChannels;
	const length = audioBuffer.length;

	// Calculate RMS (Root Mean Square) for each channel
	for (let channel = 0; channel < numChannels; channel++) {
		const channelData = audioBuffer.getChannelData(channel);
		let sumSquares = 0;

		// Calculate sum of squares for RMS
		for (let sample = 0; sample < length; sample++) {
			sumSquares += channelData[sample] * channelData[sample];
		}

		// Calculate RMS for this channel
		const rms = Math.sqrt(sumSquares / length);

		// If any channel has audio above threshold, return true
		if (rms > threshold) {
			return true;
		}
	}

	return false;
};

/**
 * Detects if audio buffer contains sound using peak amplitude analysis
 * @param audioBuffer - The AudioBuffer to analyze
 * @param threshold - Peak amplitude threshold (default: 0.005)
 * @returns true if audio contains sound, false if silent
 */
export const hasAudioContentByPeak = (
	audioBuffer: AudioBuffer,
	threshold: number = 0.005,
): boolean => {
	const numChannels = audioBuffer.numberOfChannels;
	const length = audioBuffer.length;

	// Check peak amplitude for each channel
	for (let channel = 0; channel < numChannels; channel++) {
		const channelData = audioBuffer.getChannelData(channel);

		for (let sample = 0; sample < length; sample++) {
			const amplitude = Math.abs(channelData[sample]);
			if (amplitude > threshold) {
				return true;
			}
		}
	}

	return false;
};

/**
 * Detects if audio buffer contains sound using zero-crossing rate analysis
 * Useful for detecting speech vs silence
 * @param audioBuffer - The AudioBuffer to analyze
 * @param threshold - Zero-crossing rate threshold (default: 0.1)
 * @returns true if audio contains sound, false if silent
 */
export const hasAudioContentByZeroCrossing = (
	audioBuffer: AudioBuffer,
	threshold: number = 0.1,
): boolean => {
	const numChannels = audioBuffer.numberOfChannels;
	const length = audioBuffer.length;

	for (let channel = 0; channel < numChannels; channel++) {
		const channelData = audioBuffer.getChannelData(channel);
		let zeroCrossings = 0;

		// Count zero crossings
		for (let sample = 1; sample < length; sample++) {
			if (
				(channelData[sample] >= 0 && channelData[sample - 1] < 0) ||
				(channelData[sample] < 0 && channelData[sample - 1] >= 0)
			) {
				zeroCrossings++;
			}
		}

		// Calculate zero-crossing rate
		const zeroCrossingRate = zeroCrossings / length;

		if (zeroCrossingRate > threshold) {
			return true;
		}
	}

	return false;
};

/**
 * Comprehensive sound detection using multiple analysis methods
 * @param audioBuffer - The AudioBuffer to analyze
 * @param options - Analysis options
 * @returns true if audio contains sound, false if silent
 */
export const hasAudioContentComprehensive = (
	audioBuffer: AudioBuffer,
	options: {
		peakThreshold?: number;
		requireAll?: boolean;
		rmsThreshold?: number;
		zeroCrossingThreshold?: number;
	} = {},
): boolean => {
	const {
		rmsThreshold = 0.01,
		peakThreshold = 0.005,
		zeroCrossingThreshold = 0.1,
		requireAll = false,
	} = options;

	const rmsResult = hasAudioContent(audioBuffer, rmsThreshold);
	const peakResult = hasAudioContentByPeak(audioBuffer, peakThreshold);
	const zeroCrossingResult = hasAudioContentByZeroCrossing(audioBuffer, zeroCrossingThreshold);

	if (requireAll) {
		return rmsResult && peakResult && zeroCrossingResult;
	}

	// Return true if any method detects sound (more sensitive)
	return rmsResult || peakResult || zeroCrossingResult;
};
